import React, { useEffect, useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    ReferenceInput,
    required,
    minValue,
    maxValue,
    Toolbar,
    SaveButton,
    useRecordContext,
    useDelete,
    useNotify,
    useRedirect,
    useTranslate,
    FormDataConsumer,
    useGetOne,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    EvStation,
    LocationOn,
    BatteryChargingFull,
    Settings,
    AutoAwesome,
    Build,
    QrCode,
    Language,
    Info,
    Warning,
    ArrowBack,
    Delete,
    PhotoCamera,
    RestartAlt,
} from '@mui/icons-material';
import { MultiLanguageFields } from '../sites/MultiLanguageFields';
import { PasswordInput } from '../common/PasswordInput';
import { ConnectorsSection } from './ConnectorsSection';
import { ManualCodeInput } from '../common/ManualCodeInput';
import { ChargerImageUploadField } from './ChargerImageUploadField';
import { BayNumberInput } from './BayNumberInput';
import { getSuggestedCurrent, formatCurrent } from '../../utils/powerCalculations';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const CHARGER_KNOWN_FIELDS = new Set([
    'name', 'siteId', 'loadGroupId', 'model', 'serialNumber',
    'maxPower', 'maxCurrent', 'numberPhases', 'maxSimultaneousConnectors',
    'status', 'ocppStatus', 'errorCode', 'ocppVersion',
    'firmwareVersion', 'power', 'ocppTemplateId',
    'qrCode', 'physicalLocation', 'bayNumber', 'zone', 'stationId',
    'authUsername', 'authPassword', 'authEnabled',
    'connectorType', 'connectorCount', 'manufacturer',
    'imageUrl', 'notes', 'translations', 'currentPower',
    'isManualOverride', 'manualOverrideReason', 'lastStatusUpdate',
]);

const mapChargerBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const chargerFieldLabelKey = (field: string): string =>
    CHARGER_KNOWN_FIELDS.has(field)
        ? `resources.chargers.fields.${field}`
        : field;

const DeleteChargerSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const [deleteOne] = useDelete();
    const translate = useTranslate();
    
    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };
    
    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };
    
    const handleConfirm = () => {
        if (confirmText === record?.name) {
            void deleteOne(
                'chargers',
                { id: record.id, previousData: record },
                {
                    onSuccess: () => {
                        notify(translate('resources.chargers.notifications.deleteSuccess'));
                        redirect('/chargers');
                    },
                    onError: () => {
                        notify(translate('resources.chargers.notifications.deleteError'), { type: 'error' });
                    },
                }
            );
            setOpen(false);
        }
    };
    
    const hasActiveSessions = record?.status === 'charging';
    
    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    {hasActiveSessions
                        ? translate('resources.chargers.edit.cannotDeleteWithActiveSessions')
                        : translate('resources.chargers.edit.deleteChargerDescription')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={hasActiveSessions}
                sx={{
                    borderColor: hasActiveSessions ? '#BDBDBD' : '#F44336',
                    color: hasActiveSessions ? '#BDBDBD' : '#F44336',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    flexShrink: 0,
                    '&:hover': {
                        borderColor: hasActiveSessions ? '#BDBDBD' : '#D32F2F',
                        backgroundColor: hasActiveSessions ? 'transparent' : 'rgba(244,67,54,0.08)',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={hasActiveSessions
                    ? translate('resources.chargers.edit.cannotDeleteWithActiveSessions')
                    : translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>
            
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#F44336' }} />
                        <Typography variant="h6">
                            {translate('resources.chargers.edit.deleteConfirmTitle')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{translate('resources.chargers.edit.deleteConfirmWarning')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.chargers.edit.deleteConfirmMessage')} <strong>{record?.name}</strong>.
                            {' '}{translate('resources.chargers.edit.deleteConfirmInfo')}
                        </Typography>
                    </Alert>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {translate('resources.chargers.edit.deleteConfirmPrompt')} <strong>{record?.name}</strong> {translate('resources.chargers.edit.deleteConfirmPromptBelow')}
                    </Typography>
                    
                    <TextField
                        fullWidth
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={translate('resources.chargers.edit.deleteConfirmTypePlaceholder', { name: record?.name })}
                        variant="outlined"
                        autoFocus
                        error={confirmText !== '' && confirmText !== record?.name}
                        helperText={
                            confirmText !== '' && confirmText !== record?.name
                                ? translate('resources.chargers.edit.nameDoesNotMatch')
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleClose}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('common.cancel')}
                    </Button>
                    <Button 
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={confirmText !== record?.name}
                        sx={{
                            backgroundColor: '#F44336',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#FFCDD2',
                            },
                        }}
                    >
                        {translate('resources.chargers.edit.deleteButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Wrapper that bridges react-hook-form state to ConnectorsSection's onChange callback.
// Without useFormContext.setValue the list won't re-render when connectors are added/removed.
const ConnectorsFormSection = () => {
    const { setValue, watch } = useFormContext();
    const connectors = watch('connectors') || [];
    const chargerId = watch('id');
    return (
        <ConnectorsSection
            connectors={connectors}
            onChange={(next) => setValue('connectors', next, { shouldDirty: true })}
            chargerId={chargerId}
        />
    );
};

// Component for the number_phases field in edit mode. Mirrors the create
// form's behaviour: when the selected load group is single-phase, force 1
// and disable the input (electrically required).
const NumberPhasesEditField = ({ formData, translate }: { formData: any, translate: any }) => {
    const { setValue } = useFormContext();
    const loadGroupId = formData?.loadGroupId;

    const { data: loadGroup } = useGetOne(
        'load-groups',
        { id: loadGroupId },
        { enabled: !!loadGroupId },
    );

    const isSingleLoadGroup = (loadGroup as any)?.phase === 'single';

    useEffect(() => {
        if (isSingleLoadGroup && formData?.numberPhases !== 1) {
            setValue('numberPhases', 1, { shouldValidate: true, shouldDirty: true });
        }
    }, [isSingleLoadGroup, formData?.numberPhases, setValue]);

    return (
        <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
            <SelectInput
                source="numberPhases"
                choices={[
                    { id: 1, name: translate('resources.chargers.fields.numberPhasesOne') },
                    { id: 3, name: translate('resources.chargers.fields.numberPhasesThree') },
                ]}
                disabled={isSingleLoadGroup}
                helperText={
                    isSingleLoadGroup
                        ? translate('resources.chargers.edit.numberPhasesForcedSingle')
                        : translate('resources.chargers.edit.numberPhasesHelperText')
                }
                fullWidth
            />
        </Box>
    );
};

// Component to handle max current field with auto-calculation in edit mode
const MaxCurrentEditField = ({ formData, translate }: { formData: any, translate: any }) => {
    const { setValue, watch } = useFormContext();
    const maxCurrent = watch('maxCurrent');

    // Get connector type from first connector if available
    const connectorType = formData.connectors && formData.connectors[0]
        ? formData.connectors[0].connectorType
        : formData.connectorType; // Fallback to legacy field

    const suggestedCurrent = formData.maxPower && connectorType
        ? getSuggestedCurrent(formData.maxPower, connectorType)
        : null;

    const handleAutoCalculate = () => {
        if (suggestedCurrent) {
            setValue('maxCurrent', suggestedCurrent);
        }
    };

    // Check if the current value is close to suggested (within 1A tolerance)
    const isAutoCalculated = suggestedCurrent &&
        (maxCurrent && Math.abs(maxCurrent - suggestedCurrent) < 1);

    return (
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
            <WheelSafeNumberInput
                source="maxCurrent"
                fullWidth
                step={0.1}
                min={0}
                max={1000}
                helperText={
                    suggestedCurrent
                        ? translate('resources.chargers.edit.maxCurrentHelperTextWithSuggestion', {
                            suggested: formatCurrent(suggestedCurrent)
                        })
                        : translate('resources.chargers.edit.maxCurrentHelperText')
                }
            />
            {suggestedCurrent && (
                <Tooltip title={
                    isAutoCalculated
                        ? translate('resources.chargers.edit.autoCalculated')
                        : translate('resources.chargers.edit.clickToAutoCalculate')
                }>
                    <IconButton
                        onClick={handleAutoCalculate}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            marginTop: '-12px', // Adjust for helper text
                            padding: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 200, 83, 0.08)',
                            }
                        }}
                    >
                        <AutoAwesome
                            sx={{
                                color: isAutoCalculated ? '#00C853' : '#9E9E9E',
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar 
            sx={{ 
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton 
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();
    
    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/chargers')}
                size="large"
                sx={{ 
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.chargers.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.chargers.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {record?.name} • {record?.model} • {record?.serialNumber}
                </Typography>
            </Box>
        </Box>
    );
};

export const ChargersEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    // Validation rules
    const validateRequired = [required()];
    const validateMaxPower = [required(), minValue(1), maxValue(1000)];

    // Transform function to remove read-only fields before submission
    const transformData = (data: any) => {
        const { chargerReportedStatus, manualOverrideSetBy, manualOverrideSetAt, ...rest } = data;
        // IMPORTANT: Remove chargerReportedStatus (read-only, only OCPP server can update)
        // Also remove metadata fields that are set automatically by backend
        return rest;
    };

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            transform={transformData}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.chargers.notifications.updateSuccess', { type: 'success' });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapChargerBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(chargerFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />
                    
                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <EvStation sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.chargers.edit.nameHelperText')}
                                />
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="model" 
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.modelHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="serialNumber" 
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.serialHelperText')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Image Upload Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <PhotoCamera sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.image')}
                                </Typography>
                            </Box>
                            
                            <ChargerImageUploadField
                                source="imageUrl"
                                label={translate('resources.chargers.fields.image')}
                                helperText={translate('resources.chargers.edit.imageHelperText')}
                            />
                        </CardContent>
                    </Card>
                    
                    {/* Location Assignment Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.locationAssignment')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ReferenceInput source="siteId" reference="sites">
                                    <SelectInput 
                                        optionText="name" 
                                        fullWidth
                                        validate={validateRequired}
                                        helperText={translate('resources.chargers.edit.siteHelperText')}
                                    />
                                </ReferenceInput>
                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <ReferenceInput 
                                            source="loadGroupId" 
                                            reference="load-groups"
                                            filter={{ siteId: formData.siteId }}
                                            key={formData.siteId} // Force re-render when site changes
                                        >
                                            <SelectInput 
                                                optionText="name" 
                                                fullWidth
                                                validate={validateRequired}
                                                helperText={formData.siteId 
                                                    ? translate('resources.chargers.edit.loadGroupHelperText')
                                                    : translate('resources.chargers.edit.selectSiteFirst')
                                                }
                                                disabled={!formData.siteId}
                                            />
                                        </ReferenceInput>
                                    )}
                                </FormDataConsumer>
                                <TextInput
                                    source="physicalLocation"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    helperText={translate('resources.chargers.edit.physicalLocationHelperText')}
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormDataConsumer>
                                        {({ formData }) => <BayNumberInput siteId={formData.siteId} variant="edit" />}
                                    </FormDataConsumer>
                                    <TextInput source="zone" sx={{ flex: 1 }} helperText={translate('resources.chargers.edit.zoneHelperText')} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Technical Specifications Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <BatteryChargingFull sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.technicalSpecs')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxPower"
                                            validate={validateMaxPower}
                                            fullWidth
                                            step={0.1}
                                            helperText={translate('resources.chargers.edit.maxPowerHelperText')}
                                        />
                                    </Box>
                                    <FormDataConsumer>
                                        {({ formData }) => (
                                            <MaxCurrentEditField
                                                formData={formData}
                                                translate={translate}
                                            />
                                        )}
                                    </FormDataConsumer>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="power"
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.powerDisplayHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput
                                            source="maxSimultaneousConnectors"
                                            label={translate('resources.chargers.fields.maxSimultaneousConnectors')}
                                            fullWidth
                                            min={1}
                                            max={10}
                                            step={1}
                                            helperText={translate('resources.chargers.edit.maxSimultaneousConnectorsHelperText')}
                                        />
                                    </Box>
                                    <FormDataConsumer>
                                        {({ formData }) => (
                                            <NumberPhasesEditField
                                                formData={formData}
                                                translate={translate}
                                            />
                                        )}
                                    </FormDataConsumer>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Connectors Section */}
                    <ConnectorsFormSection />

                    
                    {/* OCPP Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.ocppConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="ocppVersion"
                                            choices={[
                                                { id: '1.6', name: 'OCPP 1.6' },
                                                { id: '2.0', name: 'OCPP 2.0' },
                                                { id: '2.0.1', name: 'OCPP 2.0.1' },
                                            ]}
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.ocppVersionHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="firmwareVersion"
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.firmwareHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <ReferenceInput
                                            source="ocppTemplateId"
                                            reference="ocpp-templates"
                                            allowEmpty
                                        >
                                            <SelectInput
                                                optionText="templateName"
                                                fullWidth
                                                emptyText={translate('resources.chargers.fields.noTemplate')}
                                                helperText={translate('resources.chargers.edit.ocppTemplateHelperText')}
                                            />
                                        </ReferenceInput>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="stationId"
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.stationIdHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                        <BooleanInput
                                            source="authEnabled"
                                            helperText={translate('resources.chargers.edit.authEnabledHelperText')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="authUsername"
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.authUsernameHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <PasswordInput
                                            source="authPassword"
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.authPasswordHelperText')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Status Management Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Build sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.statusManagement')}
                                </Typography>
                            </Box>

                            {/* Show charger reported status alert if exists */}
                            <FormDataConsumer>
                                {({ formData }) => formData.chargerReportedStatus && (
                                    <Alert
                                        severity={formData.isManualOverride ? "warning" : "info"}
                                        sx={{ mb: 2 }}
                                        icon={formData.isManualOverride ? <Warning /> : <Info />}
                                    >
                                        <Typography variant="body2">
                                            <strong>{translate('resources.chargers.edit.chargerReports')}:</strong> {formData.chargerReportedStatus}
                                            {formData.isManualOverride ? (
                                                <span> ({translate('resources.chargers.edit.manualOverrideActiveMessage')})</span>
                                            ) : (
                                                <span> ({translate('resources.chargers.edit.autoSyncing')})</span>
                                            )}
                                        </Typography>
                                        {formData.isManualOverride && formData.manualOverrideSetAt && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {translate('resources.chargers.edit.overrideSet')}: {new Date(formData.manualOverrideSetAt).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Alert>
                                )}
                            </FormDataConsumer>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Effective Status and Manual Override Toggle */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="status"
                                            label={`${translate('resources.chargers.fields.status')} (${translate('resources.chargers.edit.statusEffective')})`}
                                            choices={[
                                                { id: 'available', name: translate('resources.chargers.statuses.available') },
                                                { id: 'preparing', name: translate('resources.chargers.statuses.preparing') },
                                                { id: 'charging', name: translate('resources.chargers.statuses.charging') },
                                                { id: 'suspended_ev', name: translate('resources.chargers.statuses.suspended_ev') },
                                                { id: 'suspended_evse', name: translate('resources.chargers.statuses.suspended_evse') },
                                                { id: 'finishing', name: translate('resources.chargers.statuses.finishing') },
                                                { id: 'reserved', name: translate('resources.chargers.statuses.reserved') },
                                                { id: 'faulted', name: translate('resources.chargers.statuses.faulted') },
                                                { id: 'unavailable', name: translate('resources.chargers.statuses.unavailable') },
                                                { id: 'offline', name: translate('resources.chargers.statuses.offline') },
                                                { id: 'maintenance', name: translate('resources.chargers.statuses.maintenance') },
                                                { id: 'in_use', name: translate('resources.chargers.statuses.in_use') },
                                            ]}
                                            validate={validateRequired}
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.statusEffectiveHelperText')}
                                        />
                                    </Box>

                                    {/* Manual Override Toggle */}
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <BooleanInput
                                            source="isManualOverride"
                                            label={translate('resources.chargers.edit.manualOverrideActive')}
                                            helperText={translate('resources.chargers.edit.manualOverrideHelperText')}
                                        />
                                    </Box>
                                </Box>

                                {/* Manual Override Reason */}
                                <FormDataConsumer>
                                    {({ formData }) => formData.isManualOverride && (
                                        <TextInput
                                            source="manualOverrideReason"
                                            label={translate('resources.chargers.edit.manualOverrideReason')}
                                            placeholder={translate('resources.chargers.edit.manualOverrideReasonPlaceholder')}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            helperText={translate('resources.chargers.edit.manualOverrideReasonHelperText')}
                                        />
                                    )}
                                </FormDataConsumer>

                                {/* OCPP Status Display (Read-only) */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="chargerReportedStatus"
                                            label={translate('resources.chargers.edit.chargerReportedStatusLabel')}
                                            fullWidth
                                            disabled
                                            helperText={translate('resources.chargers.edit.chargerReportedStatusHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput
                                            source="errorCode"
                                            label={translate('resources.chargers.edit.errorCodeLabel')}
                                            fullWidth
                                            helperText={translate('resources.chargers.edit.errorCodeHelperText')}
                                        />
                                    </Box>
                                </Box>

                                {/* Reservation eligibility toggle (operator long-term switch) */}
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <BooleanInput
                                            source="reservationEnabled"
                                            label={translate('resources.chargers.edit.reservationEnabledLabel')}
                                            defaultValue={true}
                                            helperText={translate('resources.chargers.edit.reservationEnabledHelperText')}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Identification Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <QrCode sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.identification')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <ManualCodeInput 
                                    source="qrCode" 
                                    label={translate('resources.chargers.fields.qrCode')}
                                    helperText={translate('resources.chargers.edit.qrCodeHelperText')}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Language sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.translations')}
                                </Typography>
                            </Box>
                            
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MultiLanguageFields 
                                        formData={formData}
                                        fields={[
                                            {
                                                name: 'name',
                                                label: translate('resources.chargers.fields.name'),
                                                required: true,
                                                helperText: translate('resources.chargers.multilang.nameHelperText')
                                            },
                                            {
                                                name: 'physicalLocation',
                                                label: translate('resources.chargers.fields.physicalLocation'),
                                                multiline: true,
                                                rows: 2,
                                                helperText: translate('resources.chargers.multilang.locationHelperText')
                                            },
                                            {
                                                name: 'notes',
                                                label: translate('resources.chargers.fields.notes'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.chargers.multilang.notesHelperText')
                                            }
                                        ]}
                                    />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>
                    
                    {/* Additional Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.additionalInfo')}
                                </Typography>
                            </Box>
                            
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {translate('resources.chargers.edit.additionalInfoMessage')}
                                </Typography>
                            </Alert>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput 
                                    source="notes" 
                                    fullWidth
                                    multiline
                                    rows={3}
                                    helperText={translate('resources.chargers.edit.notesHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Delete Charger Section */}
                    <Card sx={{ mt: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Delete sx={{ mr: 2, color: '#F44336' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.chargers.edit.deleteCharger')}
                                </Typography>
                            </Box>

                            <DeleteChargerSection />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
